<template>
  <div class="conv-card" :class="{ closed: isClosed }">
    <div class="header">
      <div class="header-main">
        <span class="id">#{{ c.conversation_id }}</span>
        <span class="right" :title="assignedLabel">{{ assignedLabel }}</span>
      </div>

      <div class="tag-row">
        <span class="pill" :class="c.status">{{ c.status }}</span>
        <span v-if="tenantLabel" class="pill tenant" :title="tenantLabel">{{ tenantLabel }}</span>
        <span v-if="channelLabel" class="pill channel" :title="channelLabel">{{ channelLabel }}</span>
        <span v-if="c.dispo_status" class="pill dispo" :title="c.dispo_status">{{ c.dispo_status }}</span>
      </div>
    </div>

    <div class="user">
      {{ c.visitor_name || "Visitor" }}
      <span v-if="c.visitor_phone">&bull; {{ c.visitor_phone }}</span>
    </div>

    <div v-if="c.form_json" class="meta">
      {{ c.form_json }}
    </div>

    <div v-if="c.last_message" class="message">
      {{ c.last_message }}
    </div>

    <div class="actions">
      <div class="action-row top-row">
        <button class="secondary" @click="$emit('view', c)">
          View Chat
        </button>

        <select v-if="!isClosed" v-model="localAssign">
          <option value="">Assign to...</option>
          <option v-for="a in agents" :key="a.id" :value="a.id">
            {{ `${a.username} (${a.state})` }}
          </option>
        </select>
      </div>

      <div v-if="!isClosed" class="action-row button-row">
        <button
          @click="assign"
          :disabled="!localAssign"
        >
          Assign
        </button>

        <button
          class="danger"
          @click="$emit('close', c.conversation_id)"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const props = defineProps({
  c: {
    type: Object,
    required: true,
  },
  agents: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["view", "assign", "close"]);

const localAssign = ref("");
const isClosed = computed(() => props.c?.status === "CLOSED");
const assignedLabel = computed(() => (
  props.c?.assigned_username ? `@${props.c.assigned_username}` : "Unassigned"
));
const tenantLabel = computed(() => firstLabel(
  props.c?.tenant_name,
  props.c?.tenantName,
  props.c?.tenant,
  props.c?.tenant_id,
  props.c?.tenantId
));
const channelLabel = computed(() => firstLabel(
  props.c?.channel_name,
  props.c?.channelName,
  props.c?.channel,
  props.c?.channel_type,
  props.c?.channelType,
  props.c?.source_channel,
  props.c?.source,
  props.c?.source_type,
  props.c?.sourceType,
  props.c?.platform
));

watch(() => props.c.conversation_id, () => {
  localAssign.value = "";
});

function firstLabel(...values) {
  for (const item of values) {
    const label = normalizeLabel(item);
    if (label) return label;
  }

  return "";
}

function normalizeLabel(value) {
  if (value === undefined || value === null) return "";

  if (typeof value === "object") {
    return firstLabel(
      value.channel_name,
      value.channelName,
      value.name,
      value.label,
      value.type,
      value.code,
      value.id
    );
  }

  const label = String(value).trim();
  if (!label) return "";

  const knownChannels = {
    WA: "WhatsApp",
    WHATSAPP: "WhatsApp",
    WEB: "Web",
    WEBCHAT: "Web Chat",
    WEB_CHAT: "Web Chat",
    CHAT_WIDGET: "Web Chat",
    WIDGET: "Web Chat",
  };

  return knownChannels[label.toUpperCase()] || label;
}

function assign() {
  if (!localAssign.value || isClosed.value) return;

  emit("assign", {
    conversation_id: props.c.conversation_id,
    agent_id: Number(localAssign.value),
  });

  localAssign.value = "";
}
</script>

<style scoped>
.conv-card {
  background: #f4f7fb;
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  transition: 0.2s;
}

.conv-card:hover {
  transform: translateY(-2px);
}

.conv-card.closed {
  background: #fafafa;
}

.header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.header-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}

.tag-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.id {
  flex: 0 0 auto;
  font-weight: 600;
}

.pill {
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  padding: 3px 8px;
  border-radius: 8px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.pill.OPEN {
  background: #e6f7ff;
  color: #1890ff;
}

.pill.CLOSED {
  background: #ffe6e6;
  color: #ff4d4f;
}

.pill.QUEUED {
  background: #fff5e6;
  color: #ad6800;
}

.pill.tenant {
  background: #eef2ff;
  color: #3730a3;
}

.pill.channel {
  background: #e6fffb;
  color: #00796b;
}

.pill.dispo {
  background: #f0f2f5;
  color: #595959;
}

.right {
  color: #666;
  flex: 0 1 auto;
  font-size: 13px;
  max-width: 140px;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user {
  margin-top: 10px;
  font-weight: 500;
}

.meta {
  color: #888;
  font-size: 12px;
  margin-top: 4px;
  word-break: break-word;
}

.message {
  margin-top: 10px;
  padding: 10px;
  background: #fff;
  border-radius: 10px;
  color: #333;
  font-size: 14px;
  word-break: break-word;
}

.actions {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.top-row {
  justify-content: space-between;
}

.button-row {
  justify-content: flex-end;
}

button {
  flex: 0 0 auto;
  padding: 6px 10px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.secondary {
  background: #e6f0ff;
}

.danger {
  background: #ffe6e6;
  color: #d9363e;
}

select {
  flex: 1 1 auto;
  min-width: 0;
  padding: 6px;
  border-radius: 8px;
}
</style>
